import { ApolloClientName } from '@/config/apolloClient';
import config from '@/config/config';
import { APP_ROUTES, NON_AUTH_PAGES } from '@/config/constants';
import { LOGIN_STATUS, MY_FEATURE_FLAGS } from '@/graphql/queries';
import { LoginStatusResponse, MyFeatureFlagsResponse } from '@/graphql/types';
import { pathMatches } from '@/utils/pathMatches';
import { useLazyQuery } from '@apollo/client';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthenticationContext from './AuthenticationContext';

type AuthenticationProviderType = {
  children: React.ReactNode;
};

enum AuthenticationModelEnum {
  api,
  mockService,
}

const AuthenticationProvider: React.FC<AuthenticationProviderType> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  axios.defaults.baseURL = config.REACT_APP_INTERNAL_BFF_BASE_URL;
  axios.defaults.withCredentials = true;
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authMode, setAuthMode] = useState<AuthenticationModelEnum>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<string>('');
  const [myFeatureFlagsQuery] = useLazyQuery<MyFeatureFlagsResponse>(MY_FEATURE_FLAGS);
  const [getLoginStatus] = useLazyQuery<LoginStatusResponse>(LOGIN_STATUS, {
    context: {
      clientName: ApolloClientName.AccountsBff,
    },
    fetchPolicy: 'network-only',
  });

  const unauthenticatedPages = [
    ...NON_AUTH_PAGES,
    APP_ROUTES.flyerDetails,
    APP_ROUTES.exploreAllFlyers,
    APP_ROUTES.exploreNewFlyers,
    APP_ROUTES.jar,
    APP_ROUTES.maps,
    APP_ROUTES.embeddedJar,
    APP_ROUTES.embeddedFlyerDetails,
  ];

  const checkUserFeatureFlags = async (): Promise<boolean> => {
    try {
      const { data } = await myFeatureFlagsQuery();
      if (data?.myFeatureFlags) {
        const featureFlagName = config.REACT_APP_FEATURE_FLAG_NAME;
        const isFlagEnabled = Object.keys(data.myFeatureFlags).includes(featureFlagName);
        return isFlagEnabled;
      }
    } catch (err) {
      console.log(err);
    }
    return false;
  };

  const isUserMasquerading = async () => {
    try {
      const { data } = await axios.get('/auth/session');
      const { identity } = data;
      if (identity.type !== 'internal' && identity.isMasquerading) {
        return true;
      }
    } catch (err) {
      console.log(err);
    }
    return false;
  };

  const checkUserAuthtication = async () => {
    try {
      const { data } = await getLoginStatus();
      if (data) {
        const { accountType, loggedIn } = data?.loginStatus;
        if (loggedIn && accountType === 'parent') {
          const featureFlagEnabled = await checkUserFeatureFlags();
          if (featureFlagEnabled || config.REACT_APP_DISABLE_FEATURE_FLAG_VERIFICATION) {
            setIsAuthenticated(true);
            setAuthMode(AuthenticationModelEnum.api);
            if (NON_AUTH_PAGES.some(url => url === pathname)) {
              navigate(APP_ROUTES.home);
            }
          } else if (!pathMatches(pathname, unauthenticatedPages)) {
            const urlRedirect = `https://${config.PEACHJAR_DOMAIN}`;
            if (!window.location.href.includes(urlRedirect)) {
              window.location.href = urlRedirect;
            }
          }
        } else if (!pathMatches(pathname, unauthenticatedPages)) {
          setIsAuthenticated(false);
          navigate(APP_ROUTES.login);
        }
        setIsLoading(false);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    checkUserAuthtication();
  }, [pathname]);

  const demoAuthentication = userEmail => {
    setUser(userEmail);
    setIsAuthenticated(true);
    localStorage.setItem('authenticatedUser', userEmail);
    setAuthMode(AuthenticationModelEnum.mockService);
    navigate(APP_ROUTES.home);
  };

  const stopMasquerading = async () => {
    try {
      await axios.post('/auth/stop-masquerading');
    } catch (err) {
      console.log('error stopping masquerading', err);
    }
  };

  const logout = async () => {
    if (authMode === AuthenticationModelEnum.api) {
      const isMasquerading = await isUserMasquerading();
      if (isMasquerading) {
        await stopMasquerading();
      }
      const logoutUrl = `${
        config.REACT_APP_ACCOUNTS_BFF_BASE_URL
      }/auth/logout?redirect=${encodeURIComponent(config.REACT_APP_BASE_URL)}/login`;
      window.location.href = logoutUrl;
    } else {
      setUser('');
      localStorage.removeItem('authenticatedUser');
      setIsAuthenticated(false);
    }
  };

  const contextState = {
    isAuthenticated,
    user,
    demoAuthentication,
    logout,
    isLoading,
  };

  return (
    <AuthenticationContext.Provider value={contextState}>{children}</AuthenticationContext.Provider>
  );
};

export default AuthenticationProvider;
